// ==============================
// Mobile card view for the User Management table
// ==============================
// On phones the 12-column table is unusable, so each user renders as a compact
// card (key fields + the same Edit/Delete actions). Used behind useIsMobile()
// in UserTable; the desktop/tablet table is unchanged.

import { ReactNode } from 'react';
import { Delete, Edit } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Card,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import { User } from '../types/UserTable.type';
import GenderChip from './common/GenderChip';
import { EmptyValue } from './common/EmptyValue';

const PLACEHOLDER_PHONE = '0909999999';

const renderEmail = (email: string | null) => {
  if (!email) return <EmptyValue />;
  return (
    <a
      href={`https://mail.google.com/mail/?view=cm&fs=1&to=${email}`}
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: 'underline', color: 'inherit' }}
    >
      {email}
    </a>
  );
};

const renderPhone = (phone: string | null) => {
  if (!phone) return <EmptyValue />;
  if (phone === PLACEHOLDER_PHONE) {
    return <span style={{ opacity: 0.4 }}>{phone}</span>;
  }
  return (
    <a
      href={`https://zalo.me/${phone}`}
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: 'underline', color: 'inherit' }}
    >
      {phone}
    </a>
  );
};

const renderParent = (parent: string | null) => {
  const isEmpty = !parent || parent.trim().toLowerCase() === 'none';
  return isEmpty ? <EmptyValue /> : parent;
};

const initials = (user: User) =>
  `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase();

// One label/value row inside a card.
const Field = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 2,
      py: 0.25,
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ textAlign: 'right', wordBreak: 'break-word' }}
      component="div"
    >
      {children}
    </Typography>
  </Box>
);

type UserCardListProps = {
  users: User[];
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
};

const UserCardList = ({ users, onEdit, onDelete }: UserCardListProps) => {
  if (users.length === 0) {
    return (
      <Box py={6} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          No users found
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1.5} sx={{ p: 1.5 }}>
      {users.map((user) => (
        <Card key={user.id} variant="outlined" sx={{ p: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1.5, minWidth: 0 }}>
              <Avatar src={user.pictureUrl} sx={{ width: 40, height: 40 }}>
                {initials(user)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={700} sx={{ lineHeight: 1.2 }}>
                  {user.firstName} {user.lastName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ID {user.id} · {user.userType || 'CHILD/ADULT'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexShrink: 0 }}>
              <Tooltip title="Edit user">
                <IconButton
                  color="primary"
                  aria-label="edit user"
                  onClick={() => onEdit(user)}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete user">
                <IconButton
                  color="error"
                  aria-label="delete user"
                  onClick={() => onDelete(user)}
                >
                  <Delete />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Divider sx={{ my: 1 }} />

          <Field label="Preferred">
            {user.preferredName || <EmptyValue />}
          </Field>
          <Field label="Business">
            {user.businessName || <EmptyValue />}
          </Field>
          <Field label="Email">{renderEmail(user.email)}</Field>
          <Field label="Phone">{renderPhone(user.phone)}</Field>
          <Field label="Parent">{renderParent(user.parent)}</Field>
          <Field label="Date of Birth">
            {new Date(user.dob).toLocaleDateString()}
          </Field>
          <Field label="Gender">
            <GenderChip value={user.gender} />
          </Field>
          <Field label="Created">
            {new Date(user.createdAt).toLocaleString()}
          </Field>
        </Card>
      ))}
    </Stack>
  );
};

export default UserCardList;
